// Shooting Evaluation model (Shooting v59): empty record, library indexing, automatic rules (§3) and ID matching (§6).
export const PHASES_TRAINING = ["PH-1", "PH-2", "PH-3"];
export const RESULT = { pass: "Pass", breaks: "Breaks down" };
export const SHOT_TYPES = [
  { key: "C&S", label: "Catch-and-shoot 3s", att: "cs_att", pct: "cs_pct", of: "all" },
  { key: "OTD", label: "Off-the-dribble 3s", att: "otd_att", pct: "otd_pct", of: "all" },
  { key: "Uncontested C&S", label: "Uncontested C&S 3s", att: "cs_uncontested_att", pct: "cs_uncontested_pct", of: "cs" },
  { key: "Contested C&S", label: "Contested C&S 3s", att: "cs_contested_att", pct: "cs_contested_pct", of: "cs" },
];
export const CHECKPOINT_PICKS = ["BS-01", "BS-02", "BS-03", "BS-04", "BS-05"];

export const EMPTY_EVAL = () => ({
  date: new Date().toISOString().slice(0, 10),
  assessed_by: "",
  layer1: {
    stats: { source: "Game stats", three_pa_per_game: null, three_pt_pct: null, cs_att: null, cs_pct: null, otd_att: null, otd_pct: null, cs_uncontested_att: null, cs_uncontested_pct: null, cs_contested_att: null, cs_contested_pct: null, shot_diet_id: null, notes: "" },
    volume_tier_id: null, pct_tier_id: null, direction_id: null, shot_type_plans: [], stat_reading_ids: [], auto: {}, overrides: {},
  },
  layer2: { phases: PHASES_TRAINING.map(id => ({ phase_id: id, result: "", footwork: [], movements: [] })), breakdown_phase_id: null, shooting_level_id: null, miss_tag_ids: [], feel_notes: "", mindset_ids: [], mindset_notes: "" },
  layer3: { flagged_mechanic_ids: [], selected_constraint_ids: [], checkpoint_ids: [] },
  layer4: { routine_ids: [] },
  notes: "",
});
export const isLegacy = (ev) => !ev.layer1;

export function indexLibrary(lib) {
  const byList = {}, itemById = {};
  for (const it of lib.lists || []) { (byList[it.list] ||= []).push(it); if (it.item_id) itemById[it.item_id] = it; }
  const mechById = Object.fromEntries((lib.mechanics || []).map(m => [m.id, m]));
  const consById = Object.fromEntries((lib.constraints || []).map(c => [c.id, c]));
  const list = (name, { activeOnly = true } = {}) => (byList[name] || []).filter(i => !activeOnly || i.status === "active");
  const nameOf = (id) => itemById[id]?.name || mechById[id]?.name || consById[id]?.name || id;
  const isRetired = (id) => (itemById[id] || mechById[id] || consById[id])?.status === "retired";
  const categories = list("Mechanic Category").map(c => c.name);
  return { version: lib.version, byList, itemById, mechById, consById, list, nameOf, isRetired, categories, mechanics: lib.mechanics || [], constraints: lib.constraints || [] };
}

export function parseDetails(s) {
  return (s || "").split(" | ").map(seg => seg.trim()).filter(Boolean).map(seg => {
    const m = seg.match(/^([A-Za-z][A-Za-z0-9 /%]{1,40}):\s*(.*)$/s);
    return m ? { label: m[1], text: m[2] } : { label: "", text: seg };
  });
}
export const detail = (item, label) => parseDetails(item?.details).find(d => d.label.toLowerCase() === label.toLowerCase())?.text || "";

// ---- Automatic rules (§3) ----
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
export const volumeTier = (pa) => { const v = num(pa); return v === null ? null : v < 1 ? "VOL-0" : v <= 2 ? "VOL-1" : v <= 4 ? "VOL-2" : "VOL-3"; };
export const pctTier = (p) => { const v = num(p); return v === null ? null : v < 30 ? "PCT-0" : v < 35 ? "PCT-1" : v < 38 ? "PCT-2" : "PCT-3"; };
const HIGH_PCT = ["PCT-2", "PCT-3"], HIGH_VOL = ["VOL-2", "VOL-3"];
export const direction = (vol, pct) => (!vol || !pct ? null : HIGH_PCT.includes(pct) ? (HIGH_VOL.includes(vol) ? "DEV-02" : "DEV-01") : HIGH_VOL.includes(vol) ? "DEV-03" : "DEV-04");
export const DIRECTION_GRID = [["DEV-01", "DEV-02"], ["DEV-04", "DEV-03"]]; // rows: high %, low % · cols: low vol, high vol

export function usage(stats) {
  const cs = num(stats.cs_att), otd = num(stats.otd_att), un = num(stats.cs_uncontested_att), con = num(stats.cs_contested_att);
  const all = cs !== null || otd !== null ? (cs || 0) + (otd || 0) : null;
  const csDen = cs !== null ? cs : un !== null || con !== null ? (un || 0) + (con || 0) : null;
  const share = (a, d) => (a === null || !d ? null : Math.round((a / d) * 1000) / 10);
  return { "C&S": share(cs, all), "OTD": share(otd, all), "Uncontested C&S": share(un, csDen), "Contested C&S": share(con, csDen) };
}
export function shotTypePlans(stats) {
  const u = usage(stats);
  return SHOT_TYPES.map(t => { const p = num(stats[t.pct]); const tier = pctTier(p); return { shot_type: t.key, usage_pct: u[t.key], pct: p, plan_id: tier ? (HIGH_PCT.includes(tier) ? "SPL-01" : "SPL-02") : null }; });
}
export function statReadings(stats) {
  const u = usage(stats), p = (k) => num(stats[k]);
  const makes = (v) => v !== null && v >= 35, misses = (v) => v !== null && v < 35, lots = (v) => v !== null && v >= 50, mostly = (v) => v !== null && v > 50;
  const out = [];
  if (lots(u["Contested C&S"]) && makes(p("cs_contested_pct"))) out.push("RD-01");
  if (lots(u["Contested C&S"]) && misses(p("cs_contested_pct"))) out.push("RD-02");
  if (mostly(u["Uncontested C&S"]) && misses(p("cs_uncontested_pct"))) out.push("RD-03");
  if (mostly(u["Uncontested C&S"]) && makes(p("cs_uncontested_pct"))) out.push("RD-04");
  if (mostly(u["C&S"]) && makes(p("cs_pct"))) out.push("RD-05");
  if (misses(p("cs_pct"))) out.push("RD-06");
  if (lots(u["OTD"]) && misses(p("otd_pct"))) out.push("RD-07");
  if (makes(p("otd_pct"))) out.push("RD-08");
  return out;
}
export function autoLayer1(stats) {
  const volume_tier_id = volumeTier(stats.three_pa_per_game), pct_tier_id = pctTier(stats.three_pt_pct);
  return { volume_tier_id, pct_tier_id, direction_id: direction(volume_tier_id, pct_tier_id), shot_type_plans: shotTypePlans(stats), stat_reading_ids: statReadings(stats) };
}
// Overrides map "field": "auto -> coach" (saved alongside the auto values).
export function overridesOf(l1) {
  const o = {};
  for (const k of ["volume_tier_id", "pct_tier_id", "direction_id"]) if ((l1.auto[k] || null) !== (l1[k] || null)) o[k] = `${l1.auto[k] || "—"} -> ${l1[k] || "—"}`;
  const a = [...(l1.auto.stat_reading_ids || [])].sort().join(","), b = [...l1.stat_reading_ids].sort().join(",");
  if (a !== b) o.stat_reading_ids = `${a || "—"} -> ${b || "—"}`;
  return o;
}

export function derive(ev) {
  let last = null, breakdown = null, allPass = true;
  for (const p of ev.layer2.phases) {
    if (p.result === RESULT.pass) last = p.phase_id;
    else { allPass = false; if (p.result === RESULT.breaks) breakdown = p.phase_id; break; }
  }
  return { level: { "PH-1": "SL-1", "PH-2": "SL-2", "PH-3": "SL-3" }[last] || "SL-0", breakdown, allPass };
}

// ---- Matching (§6) ----
export function mechanicsForTags(L, tags, breakdownPhase) {
  const hits = L.mechanics.filter(m => m.status === "active" && m.miss_tag_ids.some(t => tags.includes(t)));
  const byCat = {};
  for (const m of hits) (byCat[m.category] ||= []).push(m);
  const rank = (m) => (m.first_phase_id === breakdownPhase ? 0 : 1);
  return L.categories.filter(c => byCat[c]).map(c => ({ category: c, mechanics: byCat[c].sort((a, b) => rank(a) - rank(b)) }));
}
// Constraints that clean up a flagged mechanic first, then tag matches; Core List first inside each group.
export function constraintsSuggested(L, flagged, tags) {
  const core = (a, b) => Number(b.core) - Number(a.core);
  const active = L.constraints.filter(c => c.status === "active");
  const cleans = active.filter(c => c.mechanic_ids.some(id => flagged.includes(id))).sort(core);
  const byTag = active.filter(c => !cleans.includes(c) && c.miss_tag_ids.some(t => tags.includes(t))).sort(core);
  return [...cleans.map(c => ({ ...c, reason: "cleans" })), ...byTag.map(c => ({ ...c, reason: "tag" }))];
}
export const baseShotHits = (L, flagged) => L.list("Base Shot Checkpoint").filter(b => b.linked_ids.some(id => flagged.includes(id))).map(b => b.item_id);

export const fmtPct = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toFixed(1)}%`);
export const fmtNum = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(1));
